import apiClient from '../config/axiosConfig';
import { API_BASE_URL } from '../utils/apiUtils';

const API_URL = `${API_BASE_URL}/api/baseurls`;
const CATEGORIES_URL = `${API_BASE_URL}/api/baseurls/categories`;
const TYPES_URL = `${API_BASE_URL}/api/baseurls/types`;

const validateType = (type) => {
    if (!type || typeof type !== 'string' || type.trim() === '') {
        throw new Error('Type is required and must be a valid string');
    }
    return type.trim();
};

const validateCategory = (category) => {
    if (!category || typeof category !== 'string' || category.trim() === '') {
        throw new Error('Category is required and must be a valid string');
    }
    return category.trim();
};

const validateBaseUrlData = (baseUrlData) => {
    if (!baseUrlData || typeof baseUrlData !== 'object') {
        throw new Error('Base URL data must be a valid object');
    }

    if (!baseUrlData.url || typeof baseUrlData.url !== 'string' || baseUrlData.url.trim() === '') {
        throw new Error('URL is required and must be a valid string');
    }

    if (!baseUrlData.type || typeof baseUrlData.type !== 'string' || baseUrlData.type.trim() === '') {
        throw new Error('Type is required and must be a valid string');
    }

    try {
        new URL(baseUrlData.url);
    } catch (error) {
        throw new Error('URL must be a valid URL format');
    }

    return {
        ...baseUrlData,
        url: baseUrlData.url.trim(),
        type: baseUrlData.type.trim(),
        category: baseUrlData.category?.trim(),
        description: baseUrlData.description?.trim(),
        isActive: typeof baseUrlData.isActive === 'boolean' ? baseUrlData.isActive : true
    };
};

const validateStringValue = (value, fieldName) => {
    if (value !== null && value !== undefined && typeof value !== 'string') {
        throw new Error(`${fieldName} must be a string`);
    }
    return value?.trim();
};

const validateObjectValue = (value, fieldName) => {
    if (value && typeof value !== 'object') {
        throw new Error(`${fieldName} must be a valid object`);
    }
    return value;
};

const handleBaseUrlAuthError = (error, operation = 'API operation') => {
    if (error.response?.status === 401) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('authUser');
        throw new Error('Your session has expired. Please log in again');
    }

    if (error.response?.status === 403) {
        throw new Error('You do not have permission to access this base URL');
    }

    if (error.response?.status === 404) {
        throw new Error('Base URL not found or no longer available');
    }

    if (error.response?.status === 409) {
        throw new Error('Base URL already exists or conflicts with existing data');
    }

    if (error.response?.status === 422) {
        const validationErrors = error.response?.data?.errors || [];
        const errorMessage = validationErrors.length > 0
            ? `Validation errors: ${validationErrors.join(', ')}`
            : 'Invalid base URL data provided';
        throw new Error(errorMessage);
    }

    if (error.response?.status >= 500) {
        throw new Error('Server error. Please try again later.');
    }

    if (error.code === 'ERR_NETWORK') {
        throw new Error('Network error. Please check your connection and try again.');
    }

    if (error.code === 'ECONNABORTED') {
        throw new Error('Request timeout. Please try again.');
    }

    const errorMessage = error.response?.data?.message || error.message || 'An unexpected error occurred';
    throw new Error(errorMessage);
};

export const createBaseUrl = async (baseUrlData) => {
    try {
        const validBaseUrlData = validateBaseUrlData(baseUrlData);

        const response = await apiClient.post(`${API_URL}`, validBaseUrlData);

        if (response.status === 201 || response.status === 200) {
            return response.data;
        }
        throw new Error('Failed to create base URL');
    } catch (error) {
        handleBaseUrlAuthError(error, 'Creating base URL');
    }
};

export const updateBaseUrl = async (type, baseUrlData) => {
    try {
        const validType = validateType(type);
        const validBaseUrlData = validateObjectValue(baseUrlData, 'Base URL data');

        const response = await apiClient.put(`${API_URL}/${validType}`, validBaseUrlData);

        if (response.status === 200) {
            return response.data;
        }
        throw new Error('Failed to update base URL');
    } catch (error) {
        handleBaseUrlAuthError(error, 'Updating base URL');
    }
};

export const deleteBaseUrl = async (type) => {
    try {
        const validType = validateType(type);

        const response = await apiClient.delete(`${API_URL}/${validType}`);

        if (response.status === 200) {
            return response.data;
        }
        throw new Error('Failed to delete base URL');
    } catch (error) {
        handleBaseUrlAuthError(error, 'Deleting base URL');
    }
};

export const getBaseUrls = async () => {
    try {
        const response = await apiClient.get(`${API_URL}`);

        if (response.status === 200) {
            return response.data;
        }
        throw new Error('Failed to fetch base URLs');
    } catch (error) {
        handleBaseUrlAuthError(error, 'Fetching base URLs');
    }
};

export const getBaseUrlByType = async (type) => {
    try {
        const validType = validateType(type);

        const response = await apiClient.get(`${API_URL}/${validType}`);

        if (response.status === 200) {
            return response.data;
        }
        throw new Error('Failed to fetch base URL');
    } catch (error) {
        handleBaseUrlAuthError(error, 'Fetching base URL by type');
    }
};

export const getBaseUrlCategories = async () => {
    try {
        const response = await apiClient.get(CATEGORIES_URL);

        if (response.status === 200) {
            return response.data;
        }
        throw new Error('Failed to fetch categories');
    } catch (error) {
        handleBaseUrlAuthError(error, 'Fetching categories');
    }
};

export const getBaseUrlTypesByCategory = async (category) => {
    try {
        const validCategory = validateCategory(category);

        const response = await apiClient.get(TYPES_URL, { params: { category: validCategory } });

        if (response.status === 200) {
            return response.data;
        }
        throw new Error('Failed to fetch types for category');
    } catch (error) {
        handleBaseUrlAuthError(error, 'Fetching types by category');
    }
};

export const getBaseUrlsByCategory = async (category) => {
    try {
        const validCategory = validateCategory(category);

        const response = await apiClient.get(`${API_URL}/category/${validCategory}`);

        if (response.status === 200) {
            return response.data;
        }
        throw new Error('Failed to fetch base URLs for category');
    } catch (error) {
        handleBaseUrlAuthError(error, 'Fetching base URLs by category');
    }
};